import { DistributiveOmit, objectMap } from '@direct-frontend/stdlib';

import { FormActionsMap, FormInstance } from '../../form/types';
import { assertFormInitialized } from '../../form/utils/assertFormInitialized';
import { assertFormNotSubmitting } from '../../form/utils/assertFormNotSubmitting';
import { getFormStoreSingleton } from '../../store/store';
import { BaseValidationError } from '../../types';

import { BaseArrayField } from '../createArrayField';
import { BaseObjectField } from '../createObjectField';
import { BasePrimitiveField } from '../createPrimitiveField';
import { BaseFields, BaseFieldInstance, $canSetWhileSubmitting, SetValueOptions } from '../types';
import { BaseUnionField } from '../createUnionField';
import { $internals } from '../../form/types';
import { getOrCreateFrozenInstance } from '../../form/frozenInstancesContext';

const getInstanceWithSettersOptions = (
    fieldInstance: DistributiveOmit<BaseFieldInstance, 'asyncActions'>
): DistributiveOmit<BaseFieldInstance, 'asyncActions'> => {
    const options = { [$canSetWhileSubmitting]: true };

    const withOptions = (
        instance: DistributiveOmit<BaseFieldInstance, 'asyncActions'>
    ): DistributiveOmit<BaseFieldInstance, 'asyncActions'> => {
        if (instance.type === 'array') {
            return {
                ...instance,
                setValues: (...args: Parameters<typeof instance.setValues>): ReturnType<typeof instance.setValues> =>
                    instance.setValues(args[0], options),
                addValue: (...args: Parameters<typeof instance.addValue>): ReturnType<typeof instance.addValue> =>
                    instance.addValue(args[0], options),
                removeValue: (
                    ...args: Parameters<typeof instance.removeValue>
                ): ReturnType<typeof instance.removeValue> => instance.removeValue(args[0], options),
                insertValue: (
                    ...args: Parameters<typeof instance.insertValue>
                ): ReturnType<typeof instance.insertValue> => instance.insertValue(args[0], args[1], options),
                replaceValue: (
                    ...args: Parameters<typeof instance.replaceValue>
                ): ReturnType<typeof instance.replaceValue> => instance.replaceValue(args[0], args[1], options),
                fieldAt: (index: number) =>
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- asyncActions не нужно в этом случае
                    withOptions(instance.fieldAt(index) as DistributiveOmit<BaseFieldInstance, 'asyncActions'>),
            };
        }

        if (instance.type === 'object') {
            return {
                ...instance,
                setValue: (...args: Parameters<typeof instance.setValue>): ReturnType<typeof instance.setValue> =>
                    instance.setValue(args[0], options),
                fields: objectMap(instance.fields, innerInstance =>
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- asyncActions не нужно в этом случае
                    withOptions(innerInstance as DistributiveOmit<BaseFieldInstance, 'asyncActions'>)
                ),
            };
        }

        return {
            ...instance,
            setValue: (...args: Parameters<typeof instance.setValue>): ReturnType<typeof instance.setValue> =>
                instance.setValue(args[0], options),
        };
    };

    return withOptions(fieldInstance);
};

/** Привязывает асинхронные экшены поля с поддержкой takeLatest */
export function bindFieldAsyncActions<
    Field extends BasePrimitiveField | BaseObjectField | BaseArrayField | BaseUnionField,
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    field: Field,
    fieldId: string,
    getFieldInstance: (
        frozenFormInstance?: FormInstance<
            FormName,
            FormFields,
            FormValidationError,
            FormContextInput,
            FormContext,
            FormActions,
            FormSubmitArg,
            FormSubmitReturnValue
        >,
        options?: {
            actionsOptions?: SetValueOptions;
        }
    ) => DistributiveOmit<BaseFieldInstance, 'asyncActions'>
) {
    if (!('asyncActions' in field) || !field.asyncActions) {
        return;
    }

    const { getFieldState, updateFieldAsyncActionsMeta } = getFormStoreSingleton(form.name);

    return objectMap(field.asyncActions, (asyncAction, actionName) => (...args: unknown[]) => {
        const frozenFormInitializationId = form[$internals].formInitializationId;
        const frozenFormInstance = getOrCreateFrozenInstance(frozenFormInitializationId, form);

        const frozenFieldInstance = getFieldInstance(frozenFormInstance, {
            actionsOptions: { [$canSetWhileSubmitting]: true },
        });

        assertFormInitialized({
            formName: frozenFieldInstance.form.name,
            formId: frozenFieldInstance.form.id,
            formInitializationId: frozenFormInitializationId,
        });
        assertFormNotSubmitting(
            `Can not edit ${field.type} field value, while form is submitting`,
            {
                formName: frozenFieldInstance.form.name,
                formId: frozenFieldInstance.form.id,
                formInitializationId: frozenFormInitializationId,
            },
            fieldId
        );

        // Создаем AbortController для возможности отмены экшена
        const controller = new AbortController();

        const fieldState = getFieldState({ formInitializationId: frozenFormInitializationId, fieldId });

        // takeLatest: если экшен с таким именем уже запущен, абортим его
        const existingActionMeta = fieldState?.asyncActionsMeta?.[actionName];
        if (existingActionMeta) {
            existingActionMeta.controller.abort();
        }

        // Создаем обертку над fieldInstance, которая автоматически прокидывает опцию $canSetWhileSubmitting
        // для всех методов изменения значения, чтобы дождаться изменения в начале сабмита формы
        const fieldInstanceWithOptions = getInstanceWithSettersOptions(frozenFieldInstance);

        // Асинхронный экшен - отслеживаем промис
        const actionPromise = asyncAction(fieldInstanceWithOptions, controller.signal, ...args).finally(() => {
            // Удаляем метаданные экшена после завершения
            const state = getFieldState({ formInitializationId: frozenFormInitializationId, fieldId });
            if (state?.asyncActionsMeta?.[actionName]) {
                const newAsyncActionsMeta = { ...state.asyncActionsMeta };
                delete newAsyncActionsMeta[actionName];
                updateFieldAsyncActionsMeta({
                    formInitializationId: frozenFormInitializationId,
                    fieldId,
                    asyncActionsMeta: newAsyncActionsMeta,
                });
            }
        });

        // Добавляем метаданные экшена
        updateFieldAsyncActionsMeta({
            formInitializationId: frozenFormInitializationId,
            fieldId,
            asyncActionsMeta: {
                [actionName]: {
                    controller,
                    promise: actionPromise,
                },
            },
        });

        return actionPromise;
    });
}
